import React, { useRef } from 'react';
import {
  Search, Package, FolderUp, Drum, ChevronLeft, ChevronRight, Trash2, X,
} from 'lucide-react';

/**
 * Left-hand sidebar in Batucada: browse instrument packages, upload a folder
 * of samples, search, and clear the mapped keyboard.  Fully controlled.
 * Extracted from BatucadaRio.jsx.
 */
export const InstrumentPackageBrowser = ({
  instruments,
  selectedInstrument,
  onSelectInstrument,
  searchQuery,
  onSearchChange,
  isLoading,
  onUploadFolder,
  isUploading,
  isCollapsed,
  onToggleCollapse,
  onDeleteInstrument,
  onClearKeyboard,
  mappedKeysCount,
}) => {
  const folderInputRef = useRef(null);

  const handleFolderSelect = async (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await onUploadFolder(files);
    }
    e.target.value = '';
  };

  const handleDeleteClick = (e, instrument) => {
    e.stopPropagation();
    onDeleteInstrument?.(instrument);
  };

  if (isCollapsed) {
    return (
      <div className="w-10 bg-black/40 backdrop-blur-md border-r border-white/10 flex flex-col items-center py-3">
        <button
          onClick={onToggleCollapse}
          className="p-2 hover:bg-white/10 rounded text-gray-400 hover:text-white"
          title="Expand sidebar"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        <div className="mt-4 writing-mode-vertical text-[10px] text-gray-500 tracking-widest">
          INSTRUMENTS
        </div>
      </div>
    );
  }

  return (
    <div className="w-72 bg-black/40 backdrop-blur-md border-r border-white/10 flex flex-col">
      {/* Header */}
      <div className="p-3 border-b border-white/10 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-white tracking-tight flex items-center gap-2">
            <Package className="w-4 h-4 text-amber-400" />
            Instrument Packages
          </h2>
          <p className="text-[10px] text-gray-500 mt-0.5">Click to load samples to keyboard</p>
        </div>
        <button
          onClick={onToggleCollapse}
          className="p-1 hover:bg-white/10 rounded text-gray-400 hover:text-white"
          title="Collapse sidebar"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Upload Folder Button */}
      <div className="p-2 border-b border-white/10">
        <input
          ref={folderInputRef}
          type="file"
          webkitdirectory=""
          multiple
          onChange={handleFolderSelect}
          className="hidden"
        />
        <button
          onClick={() => folderInputRef.current?.click()}
          disabled={isUploading}
          className="w-full px-3 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/50 rounded-lg text-amber-400 text-xs font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50"
          data-testid="upload-folder-btn"
        >
          {isUploading ? (
            <>
              <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <FolderUp className="w-4 h-4" />
              Upload Instrument Folder
            </>
          )}
        </button>
        <p className="text-[9px] text-gray-500 mt-1 text-center">
          Folder name = Instrument name
        </p>
      </div>

      {/* Clear Keyboard Button */}
      {mappedKeysCount > 0 && (
        <div className="px-2 py-2 border-b border-white/10">
          <button
            onClick={onClearKeyboard}
            className="w-full px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-xs font-medium flex items-center justify-center gap-2 transition-all"
            data-testid="clear-keyboard-btn"
          >
            <X className="w-4 h-4" />
            Clear Keyboard ({mappedKeysCount} keys)
          </button>
        </div>
      )}

      {/* Search */}
      <div className="p-2 border-b border-white/10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search instruments..."
            className="w-full bg-black/50 border border-white/10 rounded-md py-2 pl-9 pr-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/50"
            data-testid="instrument-search"
          />
        </div>
      </div>

      {/* Instruments List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-center text-gray-500">Loading...</div>
        ) : instruments.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            <p>No instruments found</p>
            <p className="text-[10px] mt-1">Upload a folder with audio files</p>
          </div>
        ) : (
          instruments.map((instrument) => {
            const isSelected = selectedInstrument?.id === instrument.id;
            return (
              <div
                key={instrument.id}
                onClick={() => onSelectInstrument(instrument)}
                className={`w-full px-3 py-3 flex items-center gap-3 text-left transition-all cursor-pointer border-b border-white/5 group
                  ${isSelected
                    ? 'bg-amber-500/30 text-white'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                data-testid={`instrument-${instrument.id}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? 'bg-amber-500' : 'bg-[#333]'}`}>
                  <Drum className={`w-5 h-5 ${isSelected ? 'text-black' : 'text-amber-400'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{instrument.name}</p>
                  <p className="text-[10px] text-gray-500">
                    {instrument.sample_count || 0} samples
                  </p>
                </div>
                {isSelected ? (
                  <span className="text-xs bg-amber-500 text-black px-2 py-0.5 rounded font-medium">
                    LOADED
                  </span>
                ) : (
                  <button
                    onClick={(e) => handleDeleteClick(e, instrument)}
                    className="p-1.5 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                    title="Delete instrument"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-white/10">
        <span className="text-xs text-gray-500">{instruments.length} Instruments</span>
      </div>
    </div>
  );
};

export default InstrumentPackageBrowser;
